import React, { useState, useEffect, useMemo } from "react";
import {
  Layers,
  X,
  Calendar,
  BadgeCheck,
  Search,
  ChevronDown,
  MapPin,
  Sparkles,
  Palette,
  Users,
  Heart,
  ArrowRight,
  ImageIcon,
  Send,
} from "lucide-react";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { PrimaryButton } from "../../components/Buttons";
import LoadingImage from "../../components/LoadingImage";
import PaginationControls from "../../components/PaginationControls";
import { getMediaUrl } from "../../utils/media";
import { getPresignedDownloadUrls } from "../../utils/fileUtils";
import PortfolioViewModal from "../../components/PortfolioViewModal";
import RequestDesignModal from "../../components/RequestDesignModal";
import { useUser } from "../../context/UserContext";
import { resolveBuyerUserId } from "../../utils/buyerUserId";

/** Featured iteration may come as { designIteration: {...} } or flattened on the same object. */
function getIterationPayload(featuredIteration) {
  if (!featuredIteration) return null;
  return featuredIteration.designIteration ?? featuredIteration;
}

function parseArrayResponse(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data?.items)) return res.data.items;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

function featuredIterationKey(f) {
  if (!f) return null;
  return f.featuredDesignIterationId ?? f.featured_design_iteration_id ?? null;
}

const designThemes = {
  1: "Modern",
  2: "Minimalistic",
  3: "Traditional",
  4: "Contemporary",
};

const PAGE_SIZE = 12;

const ShortlistedPortfolios = ({ buyerId, saleData }) => {
  const { userInfo } = useUser();
  const [loading, setLoading] = useState(true);
  const [groupedByDesigner, setGroupedByDesigner] = useState({});
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [selectedDesigner, setSelectedDesigner] = useState(null);
  const [requestDesigner, setRequestDesigner] = useState(null);
  const [requestPortfolios, setRequestPortfolios] = useState([]);
  const [requestFeaturedIterations, setRequestFeaturedIterations] = useState([]);
  const [imageUrls, setImageUrls] = useState({}); // Map of s3_key to presigned URL
  const [designerRequestState, setDesignerRequestState] = useState({});
  const [selectedFeaturedIteration, setSelectedFeaturedIteration] = useState(null);
  const [removedKeys, setRemovedKeys] = useState(() => new Set());
  // Bumped after a shortlist mutation to re-sync the grouped data + per-designer
  // request state with the server (instead of relying only on optimistic local
  // removal).
  const [refreshNonce, setRefreshNonce] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTheme, setSelectedTheme] = useState("all");
  const [selectedDesignerFilter, setSelectedDesignerFilter] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const property = saleData?.propertyData || {};
  const project = property?.projectPhase?.project || {};
  const address = project?.address || {};
  const propertyId = property.propertyId || property.id || null;
  // BV-on-behalf-of-buyer: required for the BE to act with this buyer's
  // identity on the interact/uninteract endpoints. BE ignores it for
  // buyer-on-buyer calls, so it's always safe to send.
  const buyerUserId = saleData?.user?.userId || saleData?.user?.id || null;
  const buyerName = `${saleData?.user?.firstName || ""} ${saleData?.user?.lastName || ""}`.trim();
  const unitNo = property.propertyNo || "";
  const addressText = address.city && address.state
    ? `${project.projectName || ""}, ${address.city}, ${address.state}`
    : project.projectName || "";

  // Fetch presigned URLs
  useEffect(() => {
    const fetchImageUrls = async () => {
      if (!groupedByDesigner || Object.keys(groupedByDesigner).length === 0) return;

      const keySet = new Set();

      const addKey = (raw) => {
        const k = typeof raw === "string" ? raw.trim() : raw?.s3_key || raw?.s3Key || raw?.key;
        if (!k || typeof k !== "string") return;
        keySet.add(k);
      };

      Object.values(groupedByDesigner).forEach(({ portfolios, featuredIterations: feats }) => {
        (portfolios || []).forEach((portfolio) => {
          const images = portfolio?.images;
          if (!Array.isArray(images)) return;
          images.forEach((img) => {
            if (typeof img === "string") {
              if (img && !img.startsWith("data:") && !img.startsWith("/9j/") && !img.startsWith("iVBORw0KGgo")) {
                addKey(img);
              }
              return;
            }
            if (img && typeof img === "object" && (img.s3_key || img.s3Key) && !img.url && !img.downloadUrl) {
              addKey(img);
            }
          });
        });

        (feats || []).forEach((featured) => {
          const iteration = getIterationPayload(featured);
          const media = iteration?.media;
          if (!media) return;
          if (Array.isArray(media)) {
            media.forEach((m) => {
              if (m && typeof m === "object" && (m.s3_key || m.s3Key || m.key) && !m.url && !m.downloadUrl) addKey(m);
            });
          } else if (
            typeof media === "object" &&
            (media.s3_key || media.s3Key || media.key) &&
            !media.url &&
            !media.downloadUrl
          ) {
            addKey(media);
          }
        });
      });

      const uniqueS3Keys = [...keySet];
      if (uniqueS3Keys.length === 0) return;

      try {
        const presignedUrls = await getPresignedDownloadUrls(uniqueS3Keys, axiosInstance);

        const urlMap = {};
        presignedUrls.forEach((presigned, index) => {
          const downloadUrl = presigned.download_url || presigned.downloadUrl || presigned.url;
          if (!downloadUrl) return;
          const s3Key =
            presigned.s3_key || presigned.s3Key || presigned.key || uniqueS3Keys[index];
          if (s3Key) urlMap[s3Key] = downloadUrl;
        });

        setImageUrls((prev) => ({ ...prev, ...urlMap }));
      } catch (error) {
        console.error("Error fetching presigned URLs for portfolio images:", error);
      }
    };

    if (!loading && Object.keys(groupedByDesigner).length > 0) {
      fetchImageUrls();
    }
  }, [groupedByDesigner, loading]);

  const getImageSrc = (image) => {
    if (!image) return null;
    const s3Key = image?.s3_key || image?.s3Key || image?.key;
    if (typeof image === "object" && s3Key) {
      if (imageUrls[s3Key]) return imageUrls[s3Key];
      if (image.url || image.downloadUrl) return getMediaUrl(image) || image.url || image.downloadUrl;
      return null;
    }
    if (typeof image === "object" && image.url) return getMediaUrl(image);
    if (typeof image === "object" && image.base64) {
      const base64String = image.base64;
      if (base64String.startsWith("/9j/")) return `data:image/jpeg;base64,${base64String}`;
      if (base64String.startsWith("iVBORw0KGgo")) return `data:image/png;base64,${base64String}`;
      if (base64String.startsWith("data:")) return base64String;
      return `data:image/jpeg;base64,${base64String}`;
    }
    if (typeof image === "string") {
      if (imageUrls[image]) return imageUrls[image];
      if (image.startsWith("data:image/") || image.startsWith("/9j/") || image.length > 100) {
        if (image.startsWith("/9j/")) return `data:image/jpeg;base64,${image}`;
        if (image.startsWith("iVBORw0KGgo")) return `data:image/png;base64,${image}`;
        if (image.startsWith("data:")) return image;
        return `data:image/jpeg;base64,${image}`;
      }
      return getMediaUrl({ key: image });
    }
    if (image && typeof image === "object") return getMediaUrl(image);
    return null;
  };

  const formatPriceRange = (portfolio) => {
    if (portfolio.overallCost) {
      const cost = parseFloat(portfolio.overallCost);
      if (!isNaN(cost)) {
        if (cost < 100000) {
          return `₹${(cost / 1000).toFixed(0)}k - ₹${((cost * 2) / 1000).toFixed(0)}k`;
        }
        return `₹${(cost / 100000).toFixed(0)}L - ₹${((cost * 2) / 100000).toFixed(0)}L`;
      }
    }
    return "Price on request";
  };

  const formatEstimatedPrice = (cost) => {
    if (!cost) return "Price on request";
    const val = parseFloat(cost);
    if (isNaN(val)) return "Price on request";
    if (val < 100000) return `₹${(val / 1000).toFixed(0)}k`;
    return `₹${(val / 100000).toFixed(1)}L`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return String(dateStr);
    return dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const getFeaturedIterationImage = (media) => {
    if (!media) return null;
    if (Array.isArray(media) && media.length > 0) return getImageSrc(media[0]);
    if (typeof media === "object" && media !== null) return getImageSrc(media);
    return null;
  };

  useEffect(() => {
    const fetchInteractions = async () => {
      try {
        setLoading(true);

        const propId = property.propertyId || property.id || null;
        const resolvedUserId = resolveBuyerUserId(buyerId, saleData?.user);
        const isLoggedUserModel = userInfo?.persona === "PROPERTY_BUYER";

        const params = new URLSearchParams();
        if (!isLoggedUserModel) {
          if (!resolvedUserId) {
            toast.error("Please provide a user ID");
            setGroupedByDesigner({});
            setDesignerRequestState({});
            return;
          }
          params.append("userId", resolvedUserId);
        }
        if (propId) params.append("propertyId", propId);

        const queryString = params.toString();
        const portfolioUrl = queryString
          ? `/interior-designers/portfolio-items/property-interactions?${queryString}`
          : "/interior-designers/portfolio-items/property-interactions";

        const portfolioResponse = await axiosInstance.get(portfolioUrl);

        let responseData = [];
        if (Array.isArray(portfolioResponse)) responseData = portfolioResponse;
        else if (Array.isArray(portfolioResponse?.data)) responseData = portfolioResponse.data;
        else if (Array.isArray(portfolioResponse?.items)) responseData = portfolioResponse.items;

        const grouped = {};
        const requestStateByDesigner = {};

        responseData.forEach((designerGroup) => {
          const designer = designerGroup?.interiorDesigner;
          if (!designer) return;

          const designerId = designer.interiorDesignerId;
          const interactions = designerGroup?.buyerPortfolioInteractions || [];
          const portfolios = interactions.map((interaction) => interaction?.portfolioItem).filter(Boolean);

          if (!grouped[designerId]) {
            grouped[designerId] = {
              designer,
              portfolios: [],
              featuredIterations: [],
            };
          }
          grouped[designerId].portfolios = portfolios;
          requestStateByDesigner[designerId] = { hasRequest: false };
        });

        const featuredUrl = queryString
          ? `/interior-designers/featured-iterations/my-interactions?${queryString}`
          : "/interior-designers/featured-iterations/my-interactions";
        try {
          const featuredRes = await axiosInstance.get(featuredUrl);
          const featuredRows = parseArrayResponse(featuredRes);

          const mergeFeatured = (designerId, designerObj, feat) => {
            const fid = featuredIterationKey(feat);
            if (!fid) return;
            const idKey = String(designerId);
            if (!grouped[idKey]) {
              grouped[idKey] = {
                designer: designerObj,
                portfolios: [],
                featuredIterations: [],
              };
            }
            const list = grouped[idKey].featuredIterations;
            if (list.some((x) => featuredIterationKey(x) === fid)) return;
            list.push(feat);
          };

          const isFlatBuyerFeaturedInteraction = (row) =>
            row &&
            (row.buyerFeaturedIterationInteractionId != null ||
              row.featuredDesignIteration != null) &&
            !row.interiorDesigner &&
            !Array.isArray(row.buyerFeaturedInteractions) &&
            !Array.isArray(row.buyerPortfolioInteractions);

          if (featuredRows.length > 0 && isFlatBuyerFeaturedInteraction(featuredRows[0])) {
            featuredRows.forEach((ix) => {
              const featured = ix?.featuredDesignIteration || ix?.featuredIteration || null;
              if (!featured) return;
              const rawDesignerId = featured.interiorDesignerId ?? featured.interior_designer_id;
              if (rawDesignerId == null) return;
              const idKey = String(rawDesignerId);
              const designerObj =
                grouped[idKey]?.designer || {
                  interiorDesignerId: Number(rawDesignerId),
                  name: "Interior Designer",
                };
              mergeFeatured(idKey, designerObj, featured);
            });
          } else {
            featuredRows.forEach((designerGroup) => {
              const designer = designerGroup?.interiorDesigner;
              if (!designer) return;
              const designerId = String(designer.interiorDesignerId);
              const interactions =
                designerGroup?.buyerFeaturedInteractions ||
                designerGroup?.buyerFeaturedIterationInteractions ||
                designerGroup?.interactions ||
                [];

              interactions.forEach((interaction) => {
                const raw =
                  interaction?.featuredIteration ||
                  interaction?.featuredDesignIteration ||
                  (featuredIterationKey(interaction) ? interaction : null);
                if (raw) mergeFeatured(designerId, designer, raw);
              });
            });
          }
        } catch (featErr) {
          console.error("Error fetching shortlisted featured interactions:", featErr);
        }

        Object.keys(grouped).forEach((designerId) => {
          if (!requestStateByDesigner[designerId]) {
            requestStateByDesigner[designerId] = { hasRequest: false };
          }
        });

        const parseLeadsResponse = (leadResponse) => {
          if (Array.isArray(leadResponse)) return leadResponse;
          if (Array.isArray(leadResponse?.items)) return leadResponse.items;
          if (Array.isArray(leadResponse?.data?.items)) return leadResponse.data.items;
          if (Array.isArray(leadResponse?.data)) return leadResponse.data;
          return [];
        };

        const designerIds = Object.keys(grouped)
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id));

        if (resolvedUserId && propId && designerIds.length > 0) {
          const leadChecks = await Promise.all(
            designerIds.map(async (designerId) => {
              try {
                const leadResponse = await axiosInstance.get("/interior-designers/leads", {
                  params: {
                    userId: resolvedUserId,
                    propertyId: propId,
                    interiorDesignerId: designerId,
                  },
                });
                const leads = parseLeadsResponse(leadResponse);
                return [designerId, leads.length > 0];
              } catch (error) {
                console.error(`Error checking leads for designer ${designerId}:`, error);
                return [designerId, false];
              }
            })
          );

          leadChecks.forEach(([designerId, hasLead]) => {
            requestStateByDesigner[designerId] = { hasRequest: Boolean(hasLead) };
          });
        }

        Object.keys(grouped).forEach((key) => {
          const g = grouped[key];
          const hasPort = (g.portfolios || []).length > 0;
          const hasFeat = (g.featuredIterations || []).length > 0;
          if (!hasPort && !hasFeat) delete grouped[key];
        });

        setGroupedByDesigner(grouped);
        setDesignerRequestState(requestStateByDesigner);
      } catch (error) {
        console.error("Error fetching portfolio interactions:", error);
        toast.error("Failed to fetch shortlisted portfolios");
      } finally {
        setLoading(false);
      }
    };

    fetchInteractions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buyerId, saleData, userInfo?.persona, refreshNonce]);

  // Flatten grouped data into a single card list
  const flatCards = useMemo(() => {
    const cards = [];
    Object.entries(groupedByDesigner).forEach(([designerIdKey, group]) => {
      const designer = group.designer;
      const designerId = designer?.interiorDesignerId ?? designerIdKey;

      (group.portfolios || []).forEach((portfolio) => {
        const portfolioId = portfolio.interiorDesignerPortfolioItemId;
        const shortlistKey = `portfolio:${portfolioId}`;
        if (removedKeys.has(shortlistKey)) return;
        cards.push({
          kind: "PORTFOLIO_ITEM",
          designer,
          designerId,
          data: portfolio,
          themeIds: Array.isArray(portfolio.designThemeIds) ? portfolio.designThemeIds : [],
          key: `p-${portfolioId}`,
          shortlistKey,
        });
      });

      (group.featuredIterations || []).forEach((featured) => {
        const iteration = getIterationPayload(featured);
        if (!iteration) return;
        const featuredId =
          featured.featuredDesignIterationId ??
          featured.featured_design_iteration_id ??
          iteration.designIterationId ??
          iteration.design_iteration_id;
        const shortlistKey = `featured:${featuredId}`;
        if (removedKeys.has(shortlistKey)) return;
        cards.push({
          kind: "FEATURED_ITERATION",
          designer,
          designerId,
          data: featured,
          iteration,
          themeIds: [],
          key: `f-${featuredId}`,
          shortlistKey,
          featuredId,
        });
      });
    });
    return cards;
  }, [groupedByDesigner, removedKeys]);

  // Active request count for KPI
  const activeRequestCount = useMemo(() => {
    return Object.values(designerRequestState).filter((s) => s?.hasRequest).length;
  }, [designerRequestState]);

  // Theme & designer filter options
  const themeOptions = useMemo(() => {
    const set = new Set();
    flatCards.forEach((c) => c.themeIds.forEach((id) => set.add(id)));
    return Array.from(set);
  }, [flatCards]);

  const designerOptions = useMemo(() => {
    const map = new Map();
    flatCards.forEach((c) => {
      const id = c.designerId;
      const name = c.designer?.name || `Designer #${id}`;
      if (!map.has(String(id))) map.set(String(id), name);
    });
    return Array.from(map.entries());
  }, [flatCards]);

  // Apply filters
  const filteredCards = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return flatCards.filter((c) => {
      if (selectedType !== "all" && c.kind !== selectedType) return false;
      if (selectedDesignerFilter !== "all" && String(c.designerId) !== String(selectedDesignerFilter)) return false;
      if (selectedTheme !== "all" && !c.themeIds.includes(Number(selectedTheme))) return false;
      if (q) {
        const title =
          c.kind === "PORTFOLIO_ITEM"
            ? (c.data.title || "")
            : (c.iteration?.interactionName ||
                c.iteration?.interaction_name ||
                c.iteration?.iterationName ||
                c.iteration?.iteration_name ||
                c.iteration?.title ||
                "");
        const description =
          c.kind === "PORTFOLIO_ITEM"
            ? (c.data.description || "")
            : (c.iteration?.designerNotes || "");
        const designerName = c.designer?.name || "";
        const haystack = `${title} ${description} ${designerName}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [flatCards, searchQuery, selectedTheme, selectedDesignerFilter, selectedType]);

  // KPI counts (across all flat cards, ignoring filters)
  const kpiCounts = useMemo(() => {
    const themeSet = new Set();
    const designerSet = new Set();
    flatCards.forEach((c) => {
      c.themeIds.forEach((id) => themeSet.add(id));
      designerSet.add(c.designerId);
    });
    return {
      total: flatCards.length,
      themes: themeSet.size,
      designers: designerSet.size,
      active: activeRequestCount,
    };
  }, [flatCards, activeRequestCount]);

  // Pagination
  const totalFiltered = filteredCards.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageItems = filteredCards.slice(pageStart, pageStart + pageSize);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, selectedTheme, selectedDesignerFilter, selectedType, pageSize]);

  const hasActiveFilters =
    Boolean(searchQuery) ||
    selectedTheme !== "all" ||
    selectedDesignerFilter !== "all" ||
    selectedType !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedTheme("all");
    setSelectedDesignerFilter("all");
    setSelectedType("all");
  };

  const handleUnshortlist = async (card) => {
    // DELETE on the same /interact route used to create the shortlist. We
    // pass userId so BV users (who don't own the buyer's session) can act on
    // the buyer's behalf — the BE validates BV has scope to that property
    // and the buyer has a sale tied to it. Falls back to optimistic local
    // removal only if the route is missing (legacy 404/405).
    const buildQs = () => {
      const parts = [];
      if (propertyId) parts.push(`propertyId=${propertyId}`);
      if (buyerUserId) parts.push(`userId=${buyerUserId}`);
      return parts.length ? `?${parts.join("&")}` : "";
    };
    try {
      if (card.kind === "PORTFOLIO_ITEM") {
        const portfolioItemId = card.data?.interiorDesignerPortfolioItemId;
        if (!portfolioItemId) throw new Error("Missing portfolio item id");
        await axiosInstance.delete(
          `/interior-designers/portfolio-items/${portfolioItemId}/interact${buildQs()}`
        );
      } else {
        const featuredId = card.featuredId;
        if (!featuredId) throw new Error("Missing featured iteration id");
        await axiosInstance.delete(
          `/interior-designers/featured-iterations/${featuredId}/interact${buildQs()}`
        );
      }
      setRemovedKeys((prev) => {
        const next = new Set(prev);
        next.add(card.shortlistKey);
        return next;
      });
      // Re-sync with the server so the grouped data + "Request Sent" state
      // reflect the real shortlist after removal.
      setRefreshNonce((n) => n + 1);
      toast.success("Removed from shortlist");
    } catch (error) {
      // 404/405 likely means endpoint isn't exposed yet — still hide locally
      // so the BV user can keep working; warn on console for backend follow-up.
      const status = error?.response?.status;
      if (status === 404 || status === 405) {
        console.warn("Un-shortlist endpoint not available; removing locally.", error);
        setRemovedKeys((prev) => {
          const next = new Set(prev);
          next.add(card.shortlistKey);
          return next;
        });
        toast.success("Removed from shortlist");
        return;
      }
      console.error("Error removing from shortlist:", error);
      toast.error("Failed to remove from shortlist");
    }
  };

  const handleRequestDesign = (designer, portfoliosForDesigner, featuredItsForDesigner) => {
    setRequestDesigner(designer);
    setRequestPortfolios(portfoliosForDesigner || []);
    setRequestFeaturedIterations(featuredItsForDesigner || []);
  };

  const handleSendRequestForCard = (card) => {
    // Collect all not-removed shortlisted items for this designer
    const designerKey = String(card.designerId);
    const group = groupedByDesigner[designerKey] || {};
    const portfolios = (group.portfolios || []).filter(
      (p) => !removedKeys.has(`portfolio:${p.interiorDesignerPortfolioItemId}`)
    );
    const featuredIts = (group.featuredIterations || []).filter((f) => {
      const fid =
        f.featuredDesignIterationId ??
        f.featured_design_iteration_id ??
        getIterationPayload(f)?.designIterationId ??
        getIterationPayload(f)?.design_iteration_id;
      return !removedKeys.has(`featured:${fid}`);
    });
    handleRequestDesign(card.designer, portfolios, featuredIts);
  };

  const handleCloseRequestModal = () => {
    setRequestDesigner(null);
    setRequestPortfolios([]);
    setRequestFeaturedIterations([]);
  };

  const handleViewPortfolio = (portfolio, designer) => {
    setSelectedPortfolio(portfolio);
    setSelectedDesigner(designer);
  };

  const handleCloseModal = () => {
    setSelectedPortfolio(null);
    setSelectedDesigner(null);
  };

  const handleViewFeatured = (featured, designer) => {
    setSelectedFeaturedIteration({ featured, designer });
  };

  return (
    <div className="space-y-5">
      {/* Slate gradient hero band */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-600 shadow-sm">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 0%, rgba(255,255,255,0.6) 0, transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,255,255,0.4) 0, transparent 50%)",
          }}
        />
        <div className="relative px-6 py-6 sm:px-8">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-white/80 backdrop-blur-sm">
                <Sparkles size={12} />
                Buildvizer User · Shortlisted
              </div>
              <h2 className="mt-3 text-2xl sm:text-3xl font-semibold text-white">Shortlisted designs</h2>
              <p className="mt-1 text-sm text-white/70 truncate">
                {buyerName ? `${buyerName}` : "Buyer"}
                {unitNo ? ` · Unit ${unitNo}` : ""}
              </p>
              {addressText && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-white/60">
                  <MapPin size={12} />
                  <span className="truncate">{addressText}</span>
                </div>
              )}
            </div>
          </div>

          {/* KPI strip */}
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-flamingo-200 text-xs">
                <Heart size={13} />
                Total shortlisted
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.total}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Palette size={13} />
                Themes
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.themes}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-white/70 text-xs">
                <Users size={13} />
                Designers
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.designers}</div>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm ring-1 ring-white/10">
              <div className="flex items-center gap-2 text-emerald-200 text-xs">
                <Send size={13} />
                Active requests
              </div>
              <div className="mt-1 text-2xl font-semibold text-white tabular-nums">{kpiCounts.active}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search shortlisted designs…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-4 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all focus:bg-white focus:border-flamingo-400 focus:ring-2 focus:ring-flamingo-200"
            />
          </div>

          <div className="grid grid-cols-2 gap-2 lg:flex lg:items-center lg:gap-2">
            <div className="relative">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[150px]"
              >
                <option value="all">All types</option>
                <option value="FEATURED_ITERATION">Featured designs</option>
                <option value="PORTFOLIO_ITEM">Portfolios</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>

            <div className="relative">
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[150px]"
              >
                <option value="all">All themes</option>
                {themeOptions.map((id) => (
                  <option key={id} value={id}>
                    {designThemes[id] || `Theme ${id}`}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>

            <div className="relative">
              <select
                value={selectedDesignerFilter}
                onChange={(e) => setSelectedDesignerFilter(e.target.value)}
                className="appearance-none rounded-xl border border-slate-200 bg-white py-2 pl-3 pr-9 text-sm text-slate-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-flamingo-200 focus:border-flamingo-400 min-w-[170px]"
              >
                <option value="all">All designers</option>
                {designerOptions.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
            </div>
          </div>
        </div>

        {!loading && (
          <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
            <p className="text-xs text-slate-600">
              Showing{" "}
              <span className="font-semibold text-slate-800">{totalFiltered}</span> of{" "}
              <span className="font-semibold text-slate-800">{flatCards.length}</span>{" "}
              {flatCards.length === 1 ? "shortlisted item" : "shortlisted items"}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-xs font-medium text-flamingo-600 hover:text-flamingo-700"
              >
                Clear filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Loading / Empty / Grid */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
          <p className="mt-3 text-sm text-slate-500">Loading shortlisted designs…</p>
        </div>
      ) : flatCards.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Heart className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">Nothing shortlisted yet</p>
          <p className="mt-1 text-sm text-slate-500">
            This buyer hasn&apos;t shortlisted any designs. Head to <span className="font-medium text-slate-700">Browse Designs</span> to add some.
          </p>
        </div>
      ) : totalFiltered === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-12 text-center shadow-sm">
          <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
            <Search className="text-slate-400" size={24} />
          </div>
          <p className="mt-4 text-base font-medium text-slate-800">No shortlisted designs match your filters</p>
          <p className="mt-1 text-sm text-slate-500">Try clearing the search or adjusting your filters.</p>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="mt-4 text-sm font-medium text-flamingo-600 hover:text-flamingo-700"
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {pageItems.map((card) => {
              const designerName = card.designer?.name || "Interior Designer";
              const designerIdNum = card.designerId;
              const isRequestSent = Boolean(designerRequestState[designerIdNum]?.hasRequest);

              if (card.kind === "PORTFOLIO_ITEM") {
                const portfolio = card.data;
                const firstImage = portfolio.images && portfolio.images.length > 0 ? portfolio.images[0] : null;
                const imageSrc = getImageSrc(firstImage);
                const s3Key = firstImage?.s3_key || firstImage?.s3Key;
                const isImageLoading = s3Key && !imageUrls[s3Key] && !firstImage?.url && !firstImage?.downloadUrl;
                const themeNames = (portfolio.designThemeIds || [])
                  .map((id) => designThemes[id])
                  .filter(Boolean);
                const primaryTheme = themeNames[0];

                return (
                  <div
                    key={card.key}
                    className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                  >
                    <div className="relative" onClick={() => handleViewPortfolio(portfolio, card.designer)}>
                      {imageSrc ? (
                        <LoadingImage
                          src={imageSrc}
                          alt={portfolio.title}
                          containerClassName="h-44 w-full"
                          className="h-44 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : isImageLoading ? (
                        <div className="flex h-44 w-full items-center justify-center bg-slate-100">
                          <div className="h-7 w-7 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
                        </div>
                      ) : (
                        <div className="flex h-44 w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                          <ImageIcon className="h-10 w-10 text-slate-300" />
                        </div>
                      )}
                      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-2.5">
                        {primaryTheme ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-900/80 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                            {primaryTheme}
                          </span>
                        ) : <span />}
                        <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                          <Heart size={11} className="fill-white" />
                          Shortlisted
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-1 flex-col p-4">
                      <h3
                        className="truncate text-base font-semibold text-slate-900 transition-colors hover:text-flamingo-600"
                        title={portfolio.title}
                        onClick={() => handleViewPortfolio(portfolio, card.designer)}
                      >
                        {portfolio.title || "Untitled Portfolio"}
                      </h3>
                      <p className="mt-0.5 truncate text-xs font-medium text-slate-600" title={designerName}>
                        by {designerName}
                      </p>

                      {portfolio.description && (
                        <p className="mt-2 line-clamp-2 text-xs text-slate-500">{portfolio.description}</p>
                      )}

                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center rounded-full bg-flamingo-50 px-2.5 py-0.5 text-[11px] font-medium text-flamingo-700">
                          {formatPriceRange(portfolio)}
                        </span>
                        {themeNames.slice(1, 3).map((t, i) => (
                          <span key={i} className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-medium text-slate-700">
                            {t}
                          </span>
                        ))}
                      </div>

                      <div className="mt-auto pt-4">
                        <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                          <button
                            type="button"
                            onClick={() => handleUnshortlist(card)}
                            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                            title="Remove from shortlist"
                          >
                            <Heart size={13} className="fill-flamingo-500 text-flamingo-500" />
                            Un-shortlist
                          </button>
                          <PrimaryButton
                            onClick={() => handleSendRequestForCard(card)}
                            disabled={isRequestSent}
                            className="!w-auto !px-3 !py-1.5 !text-xs disabled:!opacity-60 disabled:!cursor-not-allowed flex items-center gap-1"
                            title={isRequestSent ? "Design request already sent for this property" : "Send design request"}
                          >
                            {isRequestSent ? "Request Sent" : (
                              <>
                                Send request
                                <ArrowRight size={13} />
                              </>
                            )}
                          </PrimaryButton>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              // FEATURED_ITERATION card
              const featured = card.data;
              const iteration = card.iteration;
              const firstMedia =
                Array.isArray(iteration.media) && iteration.media.length > 0
                  ? iteration.media[0]
                  : typeof iteration.media === "object" && iteration.media !== null
                    ? iteration.media
                    : null;
              const iterationImageSrc = firstMedia ? getImageSrc(firstMedia) : null;
              const firstS3Key =
                firstMedia && typeof firstMedia === "object"
                  ? firstMedia.s3_key || firstMedia.s3Key
                  : typeof firstMedia === "string"
                    ? firstMedia.trim()
                    : null;
              const isImageLoading =
                Boolean(firstS3Key) &&
                !iterationImageSrc &&
                !(typeof firstMedia === "object" && (firstMedia?.url || firstMedia?.downloadUrl)) &&
                !(typeof firstMedia === "string" && firstMedia.startsWith("data:"));
              const isFinal = Boolean(iteration.isFinal ?? iteration.is_final);
              const submittedOn =
                iteration.createdDate ||
                iteration.created_date ||
                featured.createdDate ||
                featured.created_date;
              const iterationDisplayName =
                iteration.interactionName ||
                iteration.interaction_name ||
                iteration.iterationName ||
                iteration.iteration_name ||
                iteration.title ||
                (iteration.iterationNumber != null
                  ? `Design Iteration #${iteration.iterationNumber}`
                  : "Design Iteration");

              return (
                <div
                  key={card.key}
                  className="group flex cursor-pointer flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md"
                >
                  <div className="relative" onClick={() => handleViewFeatured(featured, card.designer)}>
                    {iterationImageSrc ? (
                      <LoadingImage
                        src={iterationImageSrc}
                        alt={`Design iteration #${iteration.iterationNumber}`}
                        containerClassName="h-44 w-full"
                        className="h-44 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : isImageLoading ? (
                      <div className="flex h-44 w-full items-center justify-center bg-slate-100">
                        <div className="h-7 w-7 animate-spin rounded-full border-2 border-flamingo-200 border-t-flamingo-500" />
                      </div>
                    ) : (
                      <div className="flex h-44 w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                        <ImageIcon className="h-10 w-10 text-slate-300" />
                      </div>
                    )}
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between p-2.5">
                      <span className="inline-flex items-center gap-1 rounded-full bg-flamingo-600/95 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm backdrop-blur-sm">
                        <Layers size={11} />
                        Real Project
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-flamingo-500 to-flamingo-600 px-2.5 py-1 text-[11px] font-semibold text-white shadow-sm">
                        <Heart size={11} className="fill-white" />
                        Shortlisted
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-4">
                    <h3
                      className="truncate text-base font-semibold text-slate-900 transition-colors hover:text-flamingo-600"
                      title={iterationDisplayName}
                      onClick={() => handleViewFeatured(featured, card.designer)}
                    >
                      {iterationDisplayName}
                    </h3>
                    <p className="mt-0.5 truncate text-xs font-medium text-slate-600" title={designerName}>
                      by {designerName}
                    </p>

                    {iteration.designerNotes && (
                      <p className="mt-2 line-clamp-2 text-xs text-slate-500">{iteration.designerNotes}</p>
                    )}

                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      <span className="inline-flex items-center rounded-full bg-flamingo-50 px-2.5 py-0.5 text-[11px] font-medium text-flamingo-700">
                        {formatEstimatedPrice(iteration.estimatedPrice)}
                      </span>
                      {isFinal && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-700">
                          <BadgeCheck size={11} />
                          Final
                        </span>
                      )}
                    </div>

                    {submittedOn && (
                      <div className="mt-2 flex items-center gap-1 text-[11px] text-slate-400">
                        <Calendar size={11} />
                        <span>{formatDate(submittedOn)}</span>
                      </div>
                    )}

                    <div className="mt-auto pt-4">
                      <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-3">
                        <button
                          type="button"
                          onClick={() => handleUnshortlist(card)}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600 transition-colors"
                          title="Remove from shortlist"
                        >
                          <Heart size={13} className="fill-flamingo-500 text-flamingo-500" />
                          Un-shortlist
                        </button>
                        <PrimaryButton
                          onClick={() => handleSendRequestForCard(card)}
                          disabled={isRequestSent}
                          className="!w-auto !px-3 !py-1.5 !text-xs disabled:!opacity-60 disabled:!cursor-not-allowed flex items-center gap-1"
                          title={isRequestSent ? "Design request already sent for this property" : "Send design request"}
                        >
                          {isRequestSent ? "Request Sent" : (
                            <>
                              Send request
                              <ArrowRight size={13} />
                            </>
                          )}
                        </PrimaryButton>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Inline pagination — bare, variant="list" */}
          {totalFiltered > pageSize && (
            <PaginationControls
              pagination={{
                page: currentPage,
                pageSize,
                total: totalFiltered,
                totalPages,
                hasNext: currentPage < totalPages,
                hasPrevious: currentPage > 1,
              }}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => setPageSize(s)}
              itemName="items"
              variant="list"
              showInfo={true}
            />
          )}
        </>
      )}

      {/* Featured iteration view modal */}
      {selectedFeaturedIteration && (() => {
        const featured = selectedFeaturedIteration.featured;
        const iteration = getIterationPayload(featured);
        const imageSrc = iteration ? getFeaturedIterationImage(iteration.media) : null;
        if (!iteration) return null;
        return (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setSelectedFeaturedIteration(null)}
          >
            <div
              className="w-full max-w-3xl bg-white rounded-2xl shadow-xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">
                    {iteration.interactionName ||
                      iteration.interaction_name ||
                      iteration.iterationName ||
                      iteration.iteration_name ||
                      iteration.title ||
                      (iteration.iterationNumber != null
                        ? `Featured Design Iteration #${iteration.iterationNumber}`
                        : "Featured Design Iteration")}
                  </h3>
                  <p className="text-sm text-slate-500">
                    {selectedFeaturedIteration.designer?.name || "Interior Designer"}
                  </p>
                </div>
                <button
                  type="button"
                  className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"
                  onClick={() => setSelectedFeaturedIteration(null)}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
                {imageSrc ? (
                  <div className="aspect-video bg-slate-100 rounded-xl overflow-hidden border border-slate-200">
                    <LoadingImage
                      src={imageSrc}
                      alt={`Iteration ${iteration.iterationNumber}`}
                      containerClassName="w-full h-full"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="aspect-video bg-slate-100 rounded-xl flex items-center justify-center text-slate-400 border border-slate-200">
                    No Image
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-500">Estimated Price</p>
                    <p className="text-base font-semibold text-slate-900">
                      {formatEstimatedPrice(iteration.estimatedPrice)}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-500">Status</p>
                    <p className="text-base font-semibold text-slate-900">
                      {iteration.iterationStatus || iteration.iteration_status || "N/A"}
                    </p>
                  </div>
                  <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <p className="text-xs text-slate-500">Final</p>
                    <p className="text-base font-semibold text-slate-900">
                      {Boolean(iteration.isFinal ?? iteration.is_final) ? "Yes" : "No"}
                    </p>
                  </div>
                </div>
                {iteration.designerNotes && (
                  <div>
                    <p className="text-xs text-slate-500 mb-1">Designer Notes</p>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{iteration.designerNotes}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Portfolio View Modal */}
      <PortfolioViewModal
        isOpen={selectedPortfolio !== null}
        onClose={handleCloseModal}
        portfolio={selectedPortfolio}
        designer={selectedDesigner}
      />

      {/* Request Design Modal */}
      {saleData && requestDesigner && (() => {
        const propertyConfig = property.propertyConfig || {};
        const propertyConfigId =
          propertyConfig.propertyConfigId ||
          propertyConfig.id ||
          property.propertyConfigId ||
          null;

        const propId = property.propertyId || property.id || null;
        const userId = resolveBuyerUserId(buyerId, saleData?.user);

        if (!propertyConfigId) {
          console.warn("propertyConfigId not found. Available data:", {
            propertyConfig,
            property,
            saleData: saleData.propertyData,
          });
        }

        return (
          <RequestDesignModal
            isOpen={requestDesigner !== null}
            onClose={handleCloseRequestModal}
            onSuccess={() => setRefreshNonce((n) => n + 1)}
            designer={requestDesigner}
            portfolioItems={requestPortfolios}
            featuredIterations={requestFeaturedIterations}
            propertyId={propId}
            propertyConfigId={propertyConfigId}
            userId={userId}
            propertyInfo={{
              propertyNo: property.propertyNo,
              configName: propertyConfig.configName,
              location: (() => {
                if (address.city && address.state) {
                  return `${project.projectName || ""}, ${address.city}, ${address.state}`.trim();
                }
                return project.projectName || "";
              })(),
            }}
          />
        );
      })()}
    </div>
  );
};

export default ShortlistedPortfolios;
